// src/utils/locationMigration.js
// Auto-migration: tags all existing salonId records with a locationId/locationName
// when multi-location is first enabled. Idempotent — only updates docs missing locationId.

import {
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  doc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';

const BATCH_SIZE = 400; // safe below Firestore's 500-op limit

const COLLECTIONS_TO_MIGRATE = [
  { name: 'appointments',  staffField: false },
  { name: 'transactions',  staffField: false },
  { name: 'salonUsers',    staffField: false },
  // 'services' intentionally excluded: services are now a global catalog with no
  // locationId field. Availability at each location is derived from staff assignments.
  { name: 'staff',         staffField: true  }, // also gets locationIds array
];

async function migrateCollection(salonId, collectionName, locationId, locationName, isStaff) {
  const colRef = collection(db, collectionName);
  const q = query(colRef, where('salonId', '==', salonId));
  const snapshot = await getDocs(q);

  const docsNeedingUpdate = snapshot.docs.filter(d => !d.data().locationId);
  if (docsNeedingUpdate.length === 0) {
    console.log(`  ${collectionName}: all records already have locationId, skipping.`);
    return 0;
  }

  console.log(`  ${collectionName}: migrating ${docsNeedingUpdate.length} records → ${locationId}`);

  let updated = 0;
  for (let i = 0; i < docsNeedingUpdate.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = docsNeedingUpdate.slice(i, i + BATCH_SIZE);

    chunk.forEach(docSnap => {
      const updateData = { locationId, locationName };
      if (isStaff) {
        updateData.locationIds = [locationId];
      }
      batch.update(docSnap.ref, updateData);
    });

    await batch.commit();
    updated += chunk.length;
  }

  return updated;
}

/**
 * Run location migration for a salon.
 * Called automatically on the first save of the Contact Info page
 * when multi-location is first configured.
 *
 * @param {string} salonId
 * @param {{ locationId: string, locationName: string }} primaryLocation
 */
export async function runLocationMigration(salonId, primaryLocation) {
  const { locationId, locationName } = primaryLocation;

  if (!locationId || !locationName) {
    throw new Error('Migration requires a valid locationId and locationName');
  }

  console.log(`[Migration] Starting for salonId=${salonId}, primaryLocation=${locationId} (${locationName})`);

  for (const { name, staffField } of COLLECTIONS_TO_MIGRATE) {
    await migrateCollection(salonId, name, locationId, locationName, staffField);
  }

  // Mark migration complete on the salon document
  await updateDoc(doc(db, 'salons', salonId), {
    locationsMigrated: true,
    updatedAt: serverTimestamp()
  });

  console.log(`[Migration] Complete for salonId=${salonId}`);
}
